import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, 
  Row, 
  Col, 
  Card, 
  Badge, 
  Button, 
  ProgressBar, 
  Table,
  Alert,
  Spinner,
  ListGroup
} from 'react-bootstrap';
import { 
  FaUser, 
  FaBuilding, 
  FaFileAlt, 
  FaBell, 
  FaGraduationCap, 
  FaCog, 
  FaChartLine, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaUpload,
  FaEye,
  FaDownload,
  FaCalendarAlt,
  FaUsers,
  FaCertificate,
  FaTasks,
  FaClipboardCheck,
  FaChevronRight,
  FaInfoCircle
} from 'react-icons/fa';
import { useAuth } from '../utils/AuthContext';
import {
  fetchCompleteProfile, 
  fetchNotifications, 
  getUnreadCount,
  fetchUserDocuments 
} from '../../utils/api';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { FIELD_LABELS, SECTION_LABELS } from '../utils/profileCompletion';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({
    documentsUploaded: 0,
    activeDocuments: 0,
    pendingDocuments: 0,
    standardsCompleted: 0,
    upcomingEvents: 0
  });

  // Get company_id from user profile
  const companyId = user?.company_id;

  // Use the profile completion hook
  const {
    percentage: profileCompletion,
    isComplete: profileComplete,
    nextActions,
    completedSections,
    missingSections,
    canAccessOtherPages,
    shouldRedirectToProfile
  } = useProfileCompletion();

  const fetchDashboardData = async () => {
    if (!user?.id) {
      console.error('User ID not found');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch all dashboard data in parallel
      const [profileResponse, notificationsResponse, unreadResponse, documentsResponse] = await Promise.all([
        fetchCompleteProfile(user.id),
        fetchNotifications(user.id, false, 1, 5),
        getUnreadCount(user.id),
        fetchUserDocuments(companyId || user.id, 'type', 'all')
      ]);

      // Process profile data
      if (profileResponse.data) {
        setProfileData(profileResponse.data);
      }

      // Process notifications
      if (notificationsResponse.data) {
        const notificationData = notificationsResponse.data.notifications || notificationsResponse.data || [];
        setNotifications(Array.isArray(notificationData) ? notificationData : []);
      }

      // Process unread count
      if (unreadResponse.data) {
        const count = unreadResponse.data.unread_count || 
                     unreadResponse.data.count || 
                     0;
        setUnreadCount(count);
      }

      // Process documents
      if (documentsResponse.data) {
        const docs = documentsResponse.data.documents || documentsResponse.data || [];
        setDocuments(Array.isArray(docs) ? docs : []);
        calculateDocumentStats(docs);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('❌ Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateDocumentStats = (docs) => {
    if (!Array.isArray(docs)) {
      console.warn('Invalid documents data:', docs);
      return;
    }

    const activeDocuments = docs.filter(doc => doc.is_active).length;
    const pendingDocuments = docs.filter(doc => doc.status === 'pending').length;
    
    setStats(prev => ({ 
      ...prev, 
      documentsUploaded: docs.length,
      activeDocuments: activeDocuments,
      pendingDocuments: pendingDocuments
    }));
  };

  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
  }, [user]);

  const getCompletionColor = (percentage) => {
    if (percentage >= 100) return 'success';
    if (percentage >= 80) return 'info';
    if (percentage >= 60) return 'warning';
    return 'danger';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading dashboard...</p>
      </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Welcome Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-bold text-primary mb-1">
                Welcome back, {profileData?.profile?.firstName || profileData?.first_name || 'Member'}!
              </h2>
              <p className="text-muted mb-0">
                {profileData?.company?.companyName || profileData?.company_name || 'KPCA Member Portal'}
              </p>
            </div>
            <div className="text-end">
              <Badge bg="success" className="me-2">Active Member</Badge>
              <Badge bg="info">{new Date().toLocaleDateString()}</Badge>
            </div>
          </div>
        </Col>
      </Row>

      {/* Enhanced Profile Completion Alert */}
      {!profileComplete && (
        <Row className="mb-4">
          <Col>
            <Alert variant={getCompletionColor(profileCompletion)} className="border-0 shadow-sm">
              <div className="d-flex align-items-start">
                <div className="me-3 mt-1">
                  {profileCompletion >= 80 ? (
                    <FaInfoCircle size={20} />
                  ) : (
                    <FaExclamationTriangle size={20} />
                  )}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Complete Your Profile ({profileCompletion}%)</h5>
                    <Badge bg={getCompletionColor(profileCompletion)} className="fs-6">
                      {profileCompletion}% Complete
                    </Badge>
                  </div>
                  <ProgressBar 
                    variant={getCompletionColor(profileCompletion)}
                    now={profileCompletion} 
                    className="mb-3"
                    style={{ height: '8px' }}
                  />
                  <p className="mb-2">
                    {profileCompletion >= 80 
                      ? "You're almost done! Complete the remaining fields to unlock all member features."
                      : "Please complete your profile to access all KPCA member features and benefits."
                    }
                  </p>
                  {nextActions.length > 0 && (
                    <div className="mb-3">
                      <strong className="text-dark">Next steps:</strong>
                      <div className="row mt-2">
                        {nextActions.slice(0, 3).map((action, index) => (
                          <div key={index} className="col-md-4 mb-1">
                            <small className="d-flex align-items-center">
                              <Badge bg="primary" className="me-2">{index + 1}</Badge>
                              {action.fieldLabel}
                            </small>
                          </div>
                        ))}
                      </div>
                      {nextActions.length > 3 && (
                        <small className="text-muted">
                          + {nextActions.length - 3} more items to complete...
                        </small>
                      )}
                    </div>
                  )}
                  <div className="d-flex gap-2">
                    <Link to="/members/profile">
                      <Button variant={getCompletionColor(profileCompletion)} size="sm">
                        <FaUser className="me-1" />
                        Complete Profile
                      </Button>
                    </Link>
                    {nextActions.length > 0 && (
                      <Link to="/members/documents">
                        <Button variant="outline-secondary" size="sm">
                          <FaUpload className="me-1" />
                          Upload Documents
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Profile Complete Celebration */}
      {profileComplete && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success" className="border-0 shadow-sm">
              <div className="d-flex align-items-center">
                <FaCheckCircle size={24} className="me-3 text-success" />
                <div className="flex-grow-1">
                  <h5 className="mb-1">🎉 Profile Complete!</h5>
                  <p className="mb-0">
                    Congratulations! Your KPCA membership profile is 100% complete. You now have full access to all member features and benefits.
                  </p>
                </div>
                <Badge bg="success" className="fs-6">100%</Badge>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Quick Stats Cards */}
      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <FaUser size={24} className={`text-${getCompletionColor(profileCompletion)}`} />
              </div>
              <h6 className="text-muted mb-1">Profile Completion</h6>
              <h4 className={`fw-bold text-${getCompletionColor(profileCompletion)}`}>
                {profileCompletion}%
              </h4>
              <ProgressBar 
                variant={getCompletionColor(profileCompletion)}
                now={profileCompletion} 
                size="sm"
              />
              <small className="text-muted mt-1 d-block">
                {profileComplete ? 'All set!' : `${nextActions.length} items remaining`}
              </small>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <FaFileAlt size={24} className="text-success" />
              </div>
              <h6 className="text-muted mb-1">Documents</h6>
              <h4 className="fw-bold text-success">{stats.documentsUploaded}</h4>
              <small className="text-muted">
                {stats.activeDocuments} active, {stats.pendingDocuments} pending
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <FaBell size={24} className="text-warning" />
              </div>
              <h6 className="text-muted mb-1">Notifications</h6>
              <h4 className="fw-bold text-warning">{unreadCount}</h4>
              <small className="text-muted">unread messages</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Enhanced Profile Completion Details */}
        {!profileComplete && (
          <Col lg={4} className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Header className={`bg-${getCompletionColor(profileCompletion)} text-white`}>
                <h5 className="mb-0">
                  <FaClipboardCheck className="me-2" />
                  Profile Progress
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold">Overall Completion</span>
                    <Badge bg={getCompletionColor(profileCompletion)}>{profileCompletion}%</Badge>
                  </div>
                  <ProgressBar 
                    variant={getCompletionColor(profileCompletion)}
                    now={profileCompletion}
                    style={{ height: '10px' }}
                  />
                </div>

                {/* Section Progress */}
                <div className="mb-3">
                  <h6 className="text-muted mb-2">Section Progress:</h6>
                  {Object.entries(completedSections).map(([sectionKey, section]) => (
                    <div key={sectionKey} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="fw-bold">{SECTION_LABELS[sectionKey]}</small>
                        <small className={`text-${section.isComplete ? 'success' : 'warning'}`}>
                          {Math.round(section.percentage)}%
                        </small>
                      </div>
                      <ProgressBar 
                        variant={section.isComplete ? 'success' : 'warning'}
                        now={section.percentage}
                        size="sm"
                      />
                    </div>
                  ))}
                </div>

                {/* Next Actions */}
                {nextActions.length > 0 && (
                  <div>
                    <h6 className="text-muted mb-2">Priority Actions:</h6>
                    <div className="list-group list-group-flush">
                      {nextActions.slice(0, 4).map((action, index) => (
                        <div key={index} className="list-group-item px-0 py-2 border-0">
                          <div className="d-flex align-items-center">
                            <Badge bg="primary" className="me-2">{index + 1}</Badge>
                            <small className="flex-grow-1">{action.fieldLabel}</small>
                            <FaChevronRight size={12} className="text-muted" />
                          </div>
                        </div>
                      ))}
                    </div>
                    {nextActions.length > 4 && (
                      <div className="text-center mt-2">
                        <small className="text-muted">+ {nextActions.length - 4} more...</small>
                      </div>
                    )}
                  </div>
                )}

                <div className="d-grid mt-3">
                  <Link to="/members/profile">
                    <Button variant={getCompletionColor(profileCompletion)} className="w-100">
                      <FaUser className="me-2" />
                      Complete Profile
                    </Button>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Quick Actions */}
        <Col lg={profileComplete ? 4 : 4} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <FaTasks className="me-2" />
                Quick Actions
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Link to="/members/documents">
                  <Button variant="outline-primary" className="w-100 text-start">
                    <FaUpload className="me-2" />
                    Upload Documents
                  </Button>
                </Link>
                <Link to="/members/my-profile">
                  <Button variant="outline-success" className="w-100 text-start">
                    <FaUser className="me-2" />
                    Update Profile
                  </Button>
                </Link>
                <Link to="/members/company-profile">
                  <Button variant="outline-info" className="w-100 text-start">
                    <FaBuilding className="me-2" />
                    Company Profile
                  </Button>
                </Link>
               
                  </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Documents */}
        <Col lg={4} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <FaFileAlt className="me-2" />
                Recent Documents
              </h5>
              <Link to="/members/documents">
                <Button variant="light" size="sm">
                  View All
                </Button>
              </Link>
            </Card.Header>
            <Card.Body className="p-0">
              {documents.length === 0 ? (
                <div className="text-center py-4">
                  <FaFileAlt size={32} className="text-muted mb-2" />
                  <p className="text-muted">No documents uploaded yet</p>
                  <Link to="/members/documents">
                    <Button variant="primary" size="sm">
                      Upload First Document
                    </Button>
                  </Link>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {documents.slice(0, 5).map((doc) => (
                    <ListGroup.Item key={doc.id} className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-bold">{doc.document_type.replace(/_/g, ' ')}</div>
                        <small className="text-muted">{formatDate(doc.created_at)}</small>
                  </div>
                      <div>
                        {doc.is_active && (
                          <Badge bg="success" className="me-2">Active</Badge>
                        )}
                        <Badge bg={doc.status === 'approved' ? 'success' : doc.status === 'pending' ? 'warning' : 'secondary'}>
                          {doc.status}
                        </Badge>
                </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Notifications */}
        <Col lg={4} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-warning text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <FaBell className="me-2" />
                Recent Notifications
              </h5>
              <Link to="/members/notifications">
                <Button variant="light" size="sm">
                  View All
                </Button>
              </Link>
            </Card.Header>
            <Card.Body className="p-0">
              {notifications.length === 0 ? (
                <div className="text-center py-4">
                  <FaBell size={32} className="text-muted mb-2" />
                  <p className="text-muted">No notifications yet</p>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {notifications.slice(0, 5).map((notification) => (
                    <ListGroup.Item key={notification.id} className={`d-flex justify-content-between align-items-start ${notification.is_read ? '' : 'bg-light'}`}>
                      <div className="flex-grow-1">
                        <div className={`fw-bold ${notification.is_read ? '' : 'text-primary'}`}>
                          {notification.title}
                  </div>
                        <p className="mb-1 text-muted small">{notification.message}</p>
                        <small className="text-muted">{formatDate(notification.created_at)}</small>
                </div>
                      {!notification.is_read && (
                        <Badge bg="primary" className="ms-2">New</Badge>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* System Status & Resources */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">
                <FaChartLine className="me-2" />
                Account Status
              </h5>
            </Card.Header>
            <Card.Body>
              <Table borderless className="mb-0">
                <tbody>
                  <tr>
                    <td><strong>Membership Status:</strong></td>
                    <td>
                      <Badge bg="success">Active</Badge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Profile Completion:</strong></td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="me-2">{profileCompletion}%</span>
                        <Badge bg={getCompletionColor(profileCompletion)}>
                          {profileComplete ? 'Complete' : 'In Progress'}
                        </Badge>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Documents Uploaded:</strong></td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="me-2">{stats.documentsUploaded}</span>
                        <small className="text-muted">
                          ({stats.activeDocuments} active, {stats.pendingDocuments} pending)
                        </small>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Member Since:</strong></td>
                    <td>
                      {profileData?.company?.created_at ? 
                        formatDate(profileData.company.created_at) : 
                        'N/A'}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard; 